import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfMonth, endOfMonth, format } from "date-fns";
import { ptBR } from "date-fns/locale";

export interface TransactionWithCategory {
  id: string;
  description: string;
  amount: number;
  type: "income" | "expense";
  transaction_date: string;
  is_paid: boolean;
  category: {
    id: string;
    name: string;
    color: string;
    icon: string;
  } | null;
  account: {
    id: string;
    name: string;
  } | null;
}

export interface CategorySummary {
  id: string;
  name: string;
  color: string;
  icon: string;
  totalAmount: number;
  transactionCount: number;
  percentage: number;
  averageTransaction: number;
}

export interface DayOfWeekPattern {
  dayIndex: number;
  dayName: string;
  totalSpent: number;
  transactionCount: number;
  averageSpent: number;
}

export interface SpendingPattern {
  highestSpendingDay: DayOfWeekPattern | null;
  lowestSpendingDay: DayOfWeekPattern | null;
  weekdayVsWeekend: {
    weekdayTotal: number;
    weekendTotal: number;
    weekdayAvg: number;
    weekendAvg: number;
  };
  topMerchants: { name: string; total: number; count: number }[];
  largestTransactions: TransactionWithCategory[];
  recurringExpenses: { description: string; amount: number; occurrences: number }[];
}

export interface MonthlyReportData {
  month: Date;
  monthLabel: string;
  transactions: TransactionWithCategory[];
  totalIncome: number;
  totalExpenses: number;
  balance: number;
  categorySummaries: CategorySummary[];
  spendingPatterns: SpendingPattern;
  previousMonthComparison: {
    incomeChange: number;
    expenseChange: number;
    incomeChangePercent: number;
    expenseChangePercent: number;
  };
  dailySpending: { date: string; amount: number }[];
  isLoading: boolean;
  error: Error | null;
}

export function useReports(selectedMonth: Date): MonthlyReportData {
  const startDate = startOfMonth(selectedMonth);
  const endDate = endOfMonth(selectedMonth);

  const { data: rpcResult, isLoading, error } = useQuery({
    queryKey: ["reports", format(selectedMonth, "yyyy-MM")],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase.rpc("get_monthly_report", {
        p_year: selectedMonth.getFullYear(),
        p_month: selectedMonth.getMonth() + 1,
      });

      if (error) throw error;
      return data as any;
    },
  });

  const transactions: TransactionWithCategory[] = (rpcResult?.transactions || []).map((t: any) => ({
    ...t,
    amount: Number(t.amount),
  }));

  const totals = rpcResult?.totals || {};
  const totalIncome = Number(totals.totalIncome || 0);
  const totalExpenses = Number(totals.totalExpenses || 0);
  const balance = totalIncome - totalExpenses;

  const prevTotals = rpcResult?.previousTotals || {};
  const prevIncome = Number(prevTotals.prevIncome || 0);
  const prevExpenses = Number(prevTotals.prevExpenses || 0);

  // Category summaries from RPC
  const rawCategories = rpcResult?.categorySummaries || [];
  const categorySummaries: CategorySummary[] = rawCategories.map((cat: any) => ({
    id: cat.id,
    name: cat.name,
    color: cat.color,
    icon: cat.icon,
    totalAmount: Number(cat.totalAmount),
    transactionCount: Number(cat.transactionCount),
    percentage: totalExpenses > 0 ? (Number(cat.totalAmount) / totalExpenses) * 100 : 0,
    averageTransaction: Number(cat.transactionCount) > 0
      ? Number(cat.totalAmount) / Number(cat.transactionCount)
      : 0,
  }));

  // Day of week patterns from RPC
  const dayNames = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
  const rawDow = rpcResult?.dayOfWeekPatterns || [];
  const dayOfWeekData: DayOfWeekPattern[] = [];
  for (let i = 0; i < 7; i++) {
    const found = rawDow.find((d: any) => Number(d.dayIndex) === i);
    const totalSpent = found ? Number(found.totalSpent) : 0;
    const count = found ? Number(found.transactionCount) : 0;
    dayOfWeekData.push({
      dayIndex: i,
      dayName: dayNames[i],
      totalSpent,
      transactionCount: count,
      averageSpent: count > 0 ? totalSpent / count : 0,
    });
  }

  const sortedBySpending = [...dayOfWeekData].sort((a, b) => b.totalSpent - a.totalSpent);
  const highestSpendingDay = sortedBySpending[0]?.totalSpent > 0 ? sortedBySpending[0] : null;
  const lowestSpendingDay = sortedBySpending.filter(d => d.totalSpent > 0).pop() || null;

  const weekdayTotal = dayOfWeekData.slice(1, 6).reduce((sum, d) => sum + d.totalSpent, 0);
  const weekendTotal = dayOfWeekData[0].totalSpent + dayOfWeekData[6].totalSpent;
  const weekdayCount = dayOfWeekData.slice(1, 6).reduce((sum, d) => sum + d.transactionCount, 0);
  const weekendCount = dayOfWeekData[0].transactionCount + dayOfWeekData[6].transactionCount;

  const topMerchants = (rpcResult?.topMerchants || []).map((m: any) => ({
    name: m.name,
    total: Number(m.total),
    count: Number(m.count),
  }));

  const largestTransactions: TransactionWithCategory[] = (rpcResult?.largestTransactions || []).map((t: any) => ({
    ...t,
    amount: Number(t.amount),
  }));

  const recurringExpenses = (rpcResult?.recurringExpenses || []).map((r: any) => ({
    description: r.description,
    amount: Number(r.amount),
    occurrences: Number(r.occurrences),
  }));

  const dailySpending = (rpcResult?.dailySpending || []).map((d: any) => ({
    date: String(d.date),
    amount: Number(d.amount),
  }));

  return {
    month: selectedMonth,
    monthLabel: format(selectedMonth, "MMMM yyyy", { locale: ptBR }),
    transactions,
    totalIncome,
    totalExpenses,
    balance,
    categorySummaries,
    spendingPatterns: {
      highestSpendingDay,
      lowestSpendingDay,
      weekdayVsWeekend: {
        weekdayTotal,
        weekendTotal,
        weekdayAvg: weekdayCount > 0 ? weekdayTotal / weekdayCount : 0,
        weekendAvg: weekendCount > 0 ? weekendTotal / weekendCount : 0,
      },
      topMerchants,
      largestTransactions,
      recurringExpenses,
    },
    previousMonthComparison: {
      incomeChange: totalIncome - prevIncome,
      expenseChange: totalExpenses - prevExpenses,
      incomeChangePercent: prevIncome > 0 ? ((totalIncome - prevIncome) / prevIncome) * 100 : 0,
      expenseChangePercent: prevExpenses > 0 ? ((totalExpenses - prevExpenses) / prevExpenses) * 100 : 0,
    },
    dailySpending,
    isLoading,
    error: error as Error | null,
  };
}
